import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Download, Link2, Play, RefreshCw, Search, AlertTriangle, Info,
} from 'lucide-react';
import { domains as api, type UrlSource, type DomainUrlScan, type QuellenBericht } from '../api';
import QuellenHilfe, { QUELLEN_HILFE } from '../components/QuellenHilfe';
import SearchConsoleHilfe from '../components/SearchConsoleHilfe';
import { Blaetterleiste } from '../components/TabellenSteuerung';

/**
 * Die URL-Bestandsaufnahme einer Domain.
 *
 * EIGENE SEITE UND NICHT IM AUFKLAPPBEREICH: eine Domain kann tausende URLs
 * haben — juni.com hat 3275. Das ist keine Nebeninformation mehr, die neben
 * die Subdomains passt.
 *
 * Und anders als die Domainliste blättert diese Ansicht **serverseitig**. Bei
 * der Domainliste war es richtig, alles zu laden und im Browser zu suchen: 423
 * Zeilen sind schnell da, und die Suche bleibt dadurch über der ganzen Liste
 * richtig. Hier wären es Tausende je Domain — dann kehrt sich das Verhältnis
 * um, und Suche und Filter gehören dorthin, wo die Daten liegen.
 */

const QUELLEN_TEXT: Record<UrlSource, string> = {
  SITEMAP: 'Sitemap',
  CRAWL: 'Eigener Crawl',
  WAYBACK: 'Web-Archiv',
  COMMONCRAWL: 'Common Crawl',
  GSC: 'Search Console',
  BING: 'Bing',
  SERP: 'SERP-Dienst',
  MANUAL: 'Von Hand',
};

/**
 * Welche Quellen angeboten werden, erfragt die Seite beim Server.
 *
 * Eine fest eingebaute Liste waere ab dem ersten Mal falsch: ob SERP geht,
 * haengt an einem Schluessel, den jemand in den Einstellungen hinterlegt oder
 * wieder entfernt.
 */
const FREIE: UrlSource[] = ['SITEMAP', 'WAYBACK', 'COMMONCRAWL', 'CRAWL'];

const QUELLEN_FARBE: Record<UrlSource, string> = {
  SITEMAP: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  CRAWL: 'bg-teal-50 text-teal-700 border-teal-200',
  WAYBACK: 'bg-amber-50 text-amber-700 border-amber-200',
  COMMONCRAWL: 'bg-sky-50 text-sky-700 border-sky-200',
  GSC: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  BING: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  SERP: 'bg-violet-50 text-violet-700 border-violet-200',
  MANUAL: 'bg-slate-50 text-slate-600 border-slate-200',
};

function QuellenMarke({ quelle, titel }: { quelle: UrlSource; titel?: string }) {
  return (
    <span
      // Ohne eigenen Titel der Ein-Satz-Hinweis aus der Quellenhilfe. Wer
      // schnell wissen will, was "SERP-Dienst" heisst, soll dafuer nicht erst
      // ein Panel aufklappen muessen.
      title={titel ?? QUELLEN_HILFE[quelle]?.kurz}
      className={`inline-block rounded border px-1.5 py-0.5 text-[11px] leading-tight ${QUELLEN_FARBE[quelle]}`}
    >
      {QUELLEN_TEXT[quelle]}
    </span>
  );
}

/**
 * Der Bericht eines Durchlaufs.
 *
 * Zeigt ausdrücklich, was **nicht** vollständig ist. Eine Quelle, die bei
 * fünftausend abschneidet, ohne es zu sagen, wäre derselbe Fehler wie eine
 * Liste, die bei fünfzig aufhört — und der ist in diesem Haus schon einmal
 * teuer geworden.
 */
function ScanBericht({ scan }: { scan: DomainUrlScan }) {
  const zeilen: QuellenBericht[] = scan.bericht ?? [];
  const laeuft = scan.status === 'QUEUED' || scan.status === 'RUNNING';

  return (
    <div className="rounded-xl border border-slate-200 bg-surface p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium text-slate-700">
          {new Date(scan.startedAt).toLocaleString('de-DE')}
        </span>
        <span
          className={`rounded px-1.5 py-0.5 text-xs ${
            scan.status === 'DONE' ? 'bg-emerald-50 text-emerald-700'
            : scan.status === 'FAILED' ? 'bg-rose-50 text-rose-700'
            : 'bg-amber-50 text-amber-700'
          }`}
        >
          {laeuft ? 'läuft…' : scan.status === 'DONE' ? 'fertig' : 'gescheitert'}
        </span>
        {!laeuft && (
          <span className="text-xs text-slate-500">
            {scan.gefunden} gefunden · {scan.neu} neu
          </span>
        )}
      </div>

      {scan.error && (
        <p className="mb-2 rounded-lg bg-rose-50 px-2 py-1 text-xs text-rose-700">{scan.error}</p>
      )}

      {zeilen.length > 0 && (
        <table className="w-full text-xs">
          <tbody>
            {zeilen.map((b) => (
              <tr key={b.quelle} className="border-t border-slate-100">
                <td className="py-1 pr-2"><QuellenMarke quelle={b.quelle} /></td>
                <td className="py-1 pr-2 text-right tabular-nums text-slate-700">{b.gefunden}</td>
                <td className="py-1 pr-2 text-slate-400">
                  {b.uebersprungen > 0 && `${b.uebersprungen} Dateien weg`}
                </td>
                <td className="py-1 pr-2 text-slate-400">{Math.round(b.dauerMs / 1000)}s</td>
                <td className="py-1 text-slate-500">
                  {b.gedeckelt && (
                    <span className="mr-1 inline-flex items-center gap-1 text-amber-700">
                      <AlertTriangle size={11} /> abgeschnitten — es gibt mehr
                    </span>
                  )}
                  {b.fehler && <span className="text-rose-600">{b.fehler}</span>}
                  {!b.fehler && !b.gedeckelt && b.hinweis && (
                    <span className="text-slate-400">{b.hinweis}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function DomainUrlsPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();

  const [seite, setSeite] = useState(1);
  const [suche, setSuche] = useState('');
  const [quelle, setQuelle] = useState<UrlSource | ''>('');
  const [ohneQuelle, setOhneQuelle] = useState<UrlSource | ''>('');
  const [gewaehlt, setGewaehlt] = useState<UrlSource[]>(FREIE.filter((q) => q !== 'CRAWL'));
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const PRO_SEITE = 100;

  const { data: domain } = useQuery({ queryKey: ['domain', id], queryFn: () => api.get(id) });
  const { data: quellenInfo } = useQuery({ queryKey: ['url-quellen'], queryFn: api.urlQuellen });

  // Live bei jedem Aufruf der Seite, ohne Zwischenspeicher: eingetragen wird das
  // Dienstkonto in der Search Console, nicht hier. Ein gemerkter Wert wäre genau
  // dann falsch, wenn er gebraucht wird — direkt nachdem jemand es hinzugefügt
  // hat und wissen will, ob es gereicht hat.
  const { data: gscStand } = useQuery({
    queryKey: ['gsc-stand', id],
    queryFn: () => api.gscStand(id),
    staleTime: 0,
    // Ein nicht erreichbares Google soll die Seite nicht dreimal aufhalten.
    retry: false,
  });
  const angeboten: UrlSource[] = quellenInfo?.verfuegbar ?? FREIE;
  const { data: stats } = useQuery({
    queryKey: ['domain-url-stats', id],
    queryFn: () => api.urlStats(id),
    // Solange ein Durchlauf läuft, alle zehn Sekunden nachsehen. Er dauert
    // Minuten; ohne das müsste man raten, wann man neu lädt.
    refetchInterval: (q) => {
      const s = q.state.data?.letzterScan?.status;
      return s === 'RUNNING' || s === 'QUEUED' ? 10_000 : false;
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ['domain-urls', id, { seite, suche, quelle, ohneQuelle }],
    queryFn: () =>
      api.urls(id, {
        page: seite,
        limit: PRO_SEITE,
        q: suche || undefined,
        quelle: quelle || undefined,
        ohneQuelle: ohneQuelle || undefined,
      }),
  });

  const scanStarten = useMutation({
    mutationFn: () => api.urlScanStarten(id, { quellen: gewaehlt }),
    onSuccess: () => {
      setFehler(null);
      setMeldung('Durchlauf gestartet. Er läuft im Hintergrund und dauert einige Minuten.');
      qc.invalidateQueries({ queryKey: ['domain-url-stats', id] });
    },
    onError: (e: any) => {
      setMeldung(null);
      setFehler(e?.response?.data?.error ?? e?.message ?? 'Unbekannter Fehler');
    },
  });

  const filterAktiv = Boolean(suche || quelle || ohneQuelle);

  const exportieren = async () => {
    // Dieselben Filter wie die Liste darunter: wer eine Auswahl vor sich hat
    // und auf Ausfuhr drueckt, meint diese Auswahl.
    const blob = await api.urlsExportCsv(id, { q: suche, quelle, ohneQuelle });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // Der Dateiname sagt, ob es eine Teilmenge ist. Zwei CSVs derselben Domain
    // im Downloadordner sind sonst nicht auseinanderzuhalten.
    a.download = `urls-${domain?.name ?? id}${filterAktiv ? '-auswahl' : ''}`
      + `-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const zeilen = data?.data ?? [];
  const p = data?.pagination;
  const laeuft = stats?.letzterScan?.status === 'RUNNING' || stats?.letzterScan?.status === 'QUEUED';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/domains" className="text-slate-400 hover:text-slate-600">
          <ArrowLeft size={18} />
        </Link>
        <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900">
          <Link2 size={20} /> URLs von {domain?.unicodeName ?? domain?.name ?? '…'}
        </h1>
        <span className="text-sm text-slate-500">
          {stats?.gesamt ?? 0} bekannt
        </span>
        <div className="ml-auto flex gap-2">
          <button
            onClick={exportieren}
            disabled={!stats?.gesamt}
            title={
              filterAktiv
                ? `Exportiert die gefilterte Auswahl (${p?.total ?? 0} von ${stats?.gesamt ?? 0})`
                : 'Exportiert alle URLs dieser Domain'
            }
            className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {/* Der Knopf sagt, was er mitnimmt. Vorher hiess er immer nur
                „CSV" und nahm immer alles — auch wenn zwölf von achttausend
                Zeilen auf dem Bildschirm standen. */}
            <Download size={14} /> {filterAktiv ? `CSV (${p?.total ?? 0})` : 'CSV'}
          </button>
        </div>
      </div>

      {meldung && (
        <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{meldung}</p>
      )}
      {fehler && (
        <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-800">{fehler}</p>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        {/* ── Durchlauf anstossen ──────────────────────────────────────── */}
        <div className="space-y-3">
          {/* Über der Bestandsaufnahme, nicht darunter: ob die Search Console
              diese Domain freigibt, entscheidet mit, ob ein Durchlauf hier
              überhaupt etwas finden kann. */}
          <SearchConsoleHilfe
            stand={gscStand}
            domain={domain?.name ?? ''}
          />

          <div className="rounded-xl border border-slate-200 bg-surface p-3">
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Bestandsaufnahme
            </h2>

            <div className="mb-3 space-y-1.5">
              {angeboten.map((q) => (
                <label key={q} className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={gewaehlt.includes(q)}
                    onChange={(e) =>
                      setGewaehlt((g) => (e.target.checked ? [...g, q] : g.filter((x) => x !== q)))
                    }
                  />
                  <span>
                    <span className="text-slate-700">{QUELLEN_TEXT[q]}</span>
                    {q === 'CRAWL' && (
                      <span className="block text-xs text-amber-700">
                        Ruft die Seite selbst ab — erzeugt Last. Dauert Minuten.
                      </span>
                    )}
                    {q === 'WAYBACK' && (
                      <span className="block text-xs text-slate-400">
                        Historisch: sagt nicht, ob es die URL heute noch gibt.
                      </span>
                    )}
                    {q === 'SERP' && (
                      <span className="block text-xs text-amber-700">
                        Kostet je Abfrage. Liefert nie alles — Suchmaschinen
                        deckeln <code>site:</code> bei einigen hundert Treffern.
                      </span>
                    )}
                  </span>
                </label>
              ))}
            </div>

            <button
              onClick={() => scanStarten.mutate()}
              disabled={gewaehlt.length === 0 || laeuft || scanStarten.isPending}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {laeuft ? <RefreshCw size={14} className="animate-spin" /> : <Play size={14} />}
              {laeuft ? 'läuft…' : 'Durchlauf starten'}
            </button>

            <p className="mt-2 flex items-start gap-1 text-xs text-slate-400">
              <Info size={12} className="mt-0.5 shrink-0" />
              {angeboten.includes('SERP')
                ? 'Search Console und Bing brauchen Zugangsdaten und sind noch nicht angebunden.'
                : 'SERP braucht einen SerpApi-Schlüssel in den Einstellungen; Search Console und Bing sind noch nicht angebunden.'}
            </p>
          </div>

          {/* Was welche Quelle beigetragen hat */}
          {stats && stats.gesamt > 0 && (
            <div className="rounded-xl border border-slate-200 bg-surface p-3">
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Je Quelle
              </h2>
              <div className="space-y-1">
                {(Object.keys(QUELLEN_TEXT) as UrlSource[])
                  .filter((q) => (stats.jeQuelle[q] ?? 0) > 0)
                  .map((q) => (
                    <div key={q} className="flex items-center justify-between text-sm">
                      <QuellenMarke quelle={q} />
                      <span className="tabular-nums text-slate-600">{stats.jeQuelle[q]}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {stats?.letzterScan && <ScanBericht scan={stats.letzterScan} />}

          {/* Direkt unter den Zahlen, nicht auf einer Hilfeseite: der
              Fehlschluss entsteht beim Ansehen der Zahlen. */}
          <QuellenHilfe />
        </div>

        {/* ── Die Liste ────────────────────────────────────────────────── */}
        <div className="lg:col-span-2">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={suche}
                onChange={(e) => { setSuche(e.target.value); setSeite(1); }}
                placeholder="URL enthält…"
                className="w-56 rounded-lg border border-slate-300 py-1.5 pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>

            <select
              value={quelle}
              onChange={(e) => { setQuelle(e.target.value as UrlSource | ''); setSeite(1); }}
              className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            >
              <option value="">steht in: alle</option>
              {(Object.keys(QUELLEN_TEXT) as UrlSource[]).map((q) => (
                <option key={q} value={q}>steht in: {QUELLEN_TEXT[q]}</option>
              ))}
            </select>

            <select
              value={ohneQuelle}
              onChange={(e) => { setOhneQuelle(e.target.value as UrlSource | ''); setSeite(1); }}
              className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              // Das ist der Filter, für den das Ganze gebaut ist.
              title="Was fehlt wo? „steht in Sitemap, aber nicht in Common Crawl“"
            >
              <option value="">fehlt in: —</option>
              {(Object.keys(QUELLEN_TEXT) as UrlSource[]).map((q) => (
                <option key={q} value={q}>fehlt in: {QUELLEN_TEXT[q]}</option>
              ))}
            </select>

            {(suche || quelle || ohneQuelle) && (
              <button
                onClick={() => { setSuche(''); setQuelle(''); setOhneQuelle(''); setSeite(1); }}
                className="text-sm text-slate-500 hover:text-slate-700"
              >
                zurücksetzen
              </button>
            )}
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-surface">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    URL
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Quellen
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    zuletzt gesehen
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoading ? (
                  <tr><td colSpan={3} className="px-4 py-6 text-center text-slate-400">wird geladen…</td></tr>
                ) : zeilen.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-4 py-6 text-center text-slate-400">
                      {stats?.gesamt
                        ? 'Nichts gefunden.'
                        : 'Noch keine Bestandsaufnahme gelaufen.'}
                    </td>
                  </tr>
                ) : (
                  zeilen.map((u) => (
                    <tr key={u.id} className="hover:bg-slate-50">
                      <td className="px-4 py-2">
                        <a
                          href={u.url}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="break-all text-slate-700 hover:text-brand-700 hover:underline"
                        >
                          {u.url}
                        </a>
                      </td>
                      <td className="px-4 py-2">
                        <span className="flex flex-wrap gap-1">
                          {u.sources.map((s) => (
                            <QuellenMarke
                              key={s.source}
                              quelle={s.source}
                              titel={
                                (s.detail ? s.detail + ' · ' : '') +
                                `zuerst ${new Date(s.firstSeenAt).toLocaleDateString('de-DE')}`
                              }
                            />
                          ))}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-xs text-slate-500">
                        {new Date(u.lastSeenAt).toLocaleDateString('de-DE')}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {p && (
            <Blaetterleiste
              blaettern={{
                seite: p.page,
                seiten: p.pages,
                setSeite,
                von: p.total === 0 ? 0 : (p.page - 1) * p.limit + 1,
                bis: Math.min(p.page * p.limit, p.total),
                gesamt: p.total,
                proSeite: p.limit,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
